/**
 * multiwayClient.ts —— 多路复盘赢率的计算通道。
 *
 * 优先在 Worker 里算（避免阻塞主线程）；Worker 不可用（jsdom / 老浏览器 / 加载失败）时
 * 同步兜底，保证复盘一定能出数。仅在本手结束后的复盘中被调用。
 */

import { multiwayEquity } from '../core/multiway';
import type { MultiwayRequest, MultiwayResponse, MultiwayRowInput, MultiwayRowOutput } from './multiwayTypes';

let seq = 0;

function computeSync(rows: readonly MultiwayRowInput[]): MultiwayRowOutput[] {
  return rows.map((row) => {
    try {
      const r = multiwayEquity(row.hero, row.villains, row.board);
      return { key: row.key, equity: r.equity, exact: r.exact, boards: r.boards };
    } catch (e) {
      return { key: row.key, error: e instanceof Error ? e.message : String(e) };
    }
  });
}

/**
 * 发起一次多路复盘计算。
 * @returns dispose 函数：调用后不再回调（组件卸载 / 切换手牌时使用）。
 */
export function runMultiway(
  rows: readonly MultiwayRowInput[],
  onDone: (rows: readonly MultiwayRowOutput[]) => void,
): () => void {
  let alive = true;
  const requestId = ++seq;

  const hasWorker = typeof Worker !== 'undefined';
  if (!hasWorker) {
    const out = computeSync(rows);
    // 异步化，语义与 Worker 路径一致（UI 先渲染「计算中…」）
    const id = setTimeout(() => { if (alive) onDone(out); }, 0);
    return () => { alive = false; clearTimeout(id); };
  }

  let worker: Worker | null = null;
  const fallback = (): void => {
    const out = computeSync(rows);
    if (alive) onDone(out);
  };
  try {
    worker = new Worker(new URL('../workers/multiway.worker.ts', import.meta.url), {
      type: 'module',
      name: 'riverside-multiway',
    });
  } catch {
    fallback();
    return () => { alive = false; };
  }
  worker.onmessage = (ev: MessageEvent<MultiwayResponse>) => {
    if (!alive || ev.data.requestId !== requestId) return;
    onDone(ev.data.rows);
    try { worker?.terminate(); } catch { /* noop */ }
    worker = null;
  };
  worker.onerror = () => {
    try { worker?.terminate(); } catch { /* noop */ }
    worker = null;
    fallback();
  };
  const req: MultiwayRequest = { requestId, rows };
  worker.postMessage(req);

  return () => {
    alive = false;
    try { worker?.terminate(); } catch { /* noop */ }
    worker = null;
  };
}
